import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Slider,
  TextField,
  Tooltip
} from '@mui/material';
import { DataGrid, trTR } from '@mui/x-data-grid';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getUser } from 'src/utils/Common';
import { getDiscountRateUsers, updateDiscountRateUsers } from 'src/store/slices/dynamicCatalogSlice';
import {
  DARK,
  ORANGE,
  darkButtonSx,
  dialogPaperProps,
  inputSx,
  lightButtonSx,
  useDynamicStyles
} from 'src/components/dynamicCatalogStyles';
import DynamicListToolbar from 'src/components/DynamicListToolbar';

// Grid içinde oran göstergesi
function RateBar({ value }) {
  const v = Number(value) || 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: '#eceff1', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(Math.max(v, 0), 100)}%`, height: '100%', backgroundColor: v > 0 ? ORANGE : 'transparent' }} />
      </Box>
      <Box sx={{ width: 38, textAlign: 'right', fontWeight: 700, fontSize: '12px', color: v > 0 ? DARK : '#b0bec5' }}>%{v}</Box>
    </Box>
  );
}

// Popup içinde slider + sayı girişi
function RateEditor({ label, value, oldValue, onSliderChange, onInputChange, onBlur, classes }) {
  const changed = Number(value) !== Number(oldValue);
  return (
    <Box className={classes.dialogSection} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className={classes.sectionLabel} style={{ marginBottom: 0 }}>{label}</div>
        {changed && (
          <Box sx={{ fontSize: '11px', color: '#78909c' }}>
            %{Number(oldValue) || 0} → <b style={{ color: '#e65100' }}>%{value === '' ? 0 : value}</b>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
        <Slider
          value={typeof value === 'number' ? value : 0}
          onChange={onSliderChange}
          sx={{ color: ORANGE, flex: 1 }}
          step={5}
          min={0}
          max={100}
          marks={[{ value: 0 }, { value: 25 }, { value: 50 }, { value: 75 }, { value: 100 }]}
        />
        <TextField
          size="small"
          value={value}
          onChange={onInputChange}
          onBlur={onBlur}
          sx={{ ...inputSx, width: 96 }}
          inputProps={{ step: 5, min: 0, max: 100, type: 'number', style: { textAlign: 'right' } }}
          InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
        />
      </Box>
    </Box>
  );
}

function DiscountRateDynamic() {
  const classes = useDynamicStyles();
  const SessionUser = getUser();
  const dispatch = useDispatch();
  const [userList, setUserList] = React.useState([])
  const [rows, setRows] = React.useState([])
  const [search, setSearch] = React.useState('')
  const [selectedUser, setSelectedUser] = React.useState([])
  const [changeRateUserModal, setChangeRateUserModal] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [value, setValue] = React.useState(0);
  const [valueTopModel, setValueTopModel] = React.useState(0);

  const handleSliderChangeL = (event, newValue) => {
    setValue(newValue);
  };

  const handleInputChangeL = event => {
    setValue(event.target.value === '' ? '' : Number(event.target.value));
  };

  const handleSliderChangeLTopModel = (event, newValue) => {
    setValueTopModel(newValue);
  };

  const handleInputChangeLTopModel = event => {
    setValueTopModel(event.target.value === '' ? '' : Number(event.target.value));
  };

  const handleBlur = () => {
    if (value < 0) {
      setValue(0);
    } else if (value > 100) {
      setValue(100);
    }

    if (valueTopModel < 0) {
      setValueTopModel(0);
    } else if (valueTopModel > 100) {
      setValueTopModel(100);
    }
  };

  const loadUsers = () => {
    setLoading(true)
    dispatch(getDiscountRateUsers()).then(response => {
      if (response.payload?.length > 0) {
        setUserList(response.payload)
      }
      setLoading(false)
    })
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const q = search.toLocaleLowerCase('tr-TR');
    const searchedRows = userList.filter(i =>
      (i.ADSOYAD || '').toLocaleLowerCase('tr-TR').includes(q) || (i.SICILNO || '').toString().includes(search))
    setRows(searchedRows)
  }, [search, userList]);

  const handleUpdateRoleClick = (row) => {
    setSelectedUser(row)
    setChangeRateUserModal(true)
    setValue(row.UcretliDegisimIndirimOranlari)
    setValueTopModel(row.UstModelFarkIndirimOran)
  }

  const closeModal = () => {
    if (saving) return;
    setChangeRateUserModal(false);
  }

  const updateDiscountRateClick = () => {
    const data = {
      UserId: selectedUser.ID,
      UcretliDegisimIndirimOran: value,
      UstModelFarkIndirimOran: valueTopModel,
      SessionUserId: SessionUser?.UserId
    }

    setSaving(true)
    dispatch(updateDiscountRateUsers(data)).then(() => {
      setSaving(false)
      setChangeRateUserModal(false)
      setSelectedUser([])
      loadUsers();
    })
  }

  const columns = [
    { field: 'ADSOYAD', headerName: 'Ad Soyad', flex: 1.4, minWidth: 180 },
    { field: 'SICILNO', headerName: 'Sicil No', width: 130 },
    {
      field: 'UcretliDegisimIndirimOranlari', headerName: 'Ücretli Değişim İndirim Oranı', flex: 1, minWidth: 200,
      renderCell: (p) => <RateBar value={p.value} />
    },
    {
      field: 'UstModelFarkIndirimOran', headerName: 'Üst Model Fark İndirim Oranı', flex: 1, minWidth: 200,
      renderCell: (p) => <RateBar value={p.value} />
    },
    {
      field: 'Güncelle', headerName: 'İşlem', width: 90, align: 'center', headerAlign: 'center',
      sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (cellValues) => (
        <Tooltip title="Düzenle">
          <IconButton size="small" onClick={() => handleUpdateRoleClick(cellValues.row)}>
            <EditIcon sx={{ fontSize: 18, color: ORANGE }} />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const unchanged =
    Number(value) === Number(selectedUser?.UcretliDegisimIndirimOranlari) &&
    Number(valueTopModel) === Number(selectedUser?.UstModelFarkIndirimOran);

  return (
    <>
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Kişi Bazlı İndirim Oranları"
          subheader={loading ? 'Yükleniyor…' : `${userList.length} kullanıcı`}
        />
        <DynamicListToolbar
          searchText={search}
          onSearchChange={setSearch}
          placeholder="Ad soyad veya sicil no ara"
          shownCount={rows.length}
        />
        <DataGrid
          className={classes.grid}
          style={{ height: 560, width: '100%' }}
          columns={columns}
          rows={rows}
          loading={loading}
          rowHeight={44}
          headerHeight={44}
          disableSelectionOnClick
          getRowId={(row) => row.ID}
          localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
        />
      </Card>

      <Dialog open={changeRateUserModal} onClose={closeModal} fullWidth maxWidth="sm" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          İndirim Oranı Düzenle
          <IconButton size="small" onClick={closeModal} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '10px 14px', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: DARK, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PersonIcon sx={{ color: ORANGE, fontSize: 20 }} />
            </Box>
            <Box>
              <Box sx={{ fontSize: '14px', fontWeight: 700, color: '#263238' }}>{selectedUser?.ADSOYAD}</Box>
              <Box sx={{ fontSize: '11px', color: '#78909c' }}>Sicil No: {selectedUser?.SICILNO}</Box>
            </Box>
          </Box>
          <RateEditor
            label="Ücretli Değişim İndirim Oranı"
            value={value}
            oldValue={selectedUser?.UcretliDegisimIndirimOranlari}
            onSliderChange={handleSliderChangeL}
            onInputChange={handleInputChangeL}
            onBlur={handleBlur}
            classes={classes}
          />
          <RateEditor
            label="Üst Model Fark İndirim Oranı"
            value={valueTopModel}
            oldValue={selectedUser?.UstModelFarkIndirimOran}
            onSliderChange={handleSliderChangeLTopModel}
            onInputChange={handleInputChangeLTopModel}
            onBlur={handleBlur}
            classes={classes}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={closeModal} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 110 }}
            onClick={updateDiscountRateClick}
            disabled={saving || unchanged || value === '' || valueTopModel === ''}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: ORANGE }} /> : null}
          >
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DiscountRateDynamic;
